import { Component, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { AlertTriangle, Edit3, Flame, Gift, Save, Sparkles, Ticket, Trash2, Volume2 } from "lucide-react";
import { toast } from "sonner";
import ColorPicker from "../components/ColorPicker";
import IconPicker from "../components/IconPicker";
import { CurrencyIcon, currencyAmountLabel, currencyLabel } from "../lib/currencyDisplay";

const TYPE_LABELS = {
  free_currency: "Free Currency",
  paid_currency: "Paid Currency",
  giveaway_currency: "Giveaway Currency",
  event_currency: "Event Currency",
};

const RESET_TEXT = "RESET CURRENCY";
const FALLBACK_COLORS = {
  free_currency: "#ff9f0a",
  paid_currency: "#30d158",
  giveaway_currency: "#64d2ff",
  event_currency: "#bf5af2",
};
const toNumber = (value, fallback = 0) => {
  const parsed = globalThis.Number(value);
  return globalThis.Number.isFinite(parsed) ? parsed : fallback;
};

export default function Currencies() {
  const [list, setList] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [grant, setGrant] = useState({ viewer_id: "", amount: 0, reason: "", currency_id: "paid_currency" });
  const [lastGrant, setLastGrant] = useState(null);
  const [reset, setReset] = useState({ login: "", currency_id: "", scope: "user", confirmation: "" });
  const [settings, setSettings] = useState(null);
  const [runtimeFiles, setRuntimeFiles] = useState([]);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoadError("");
    const [c, v, s, f] = await Promise.allSettled([
      api.get("/currencies"),
      api.get("/viewers?limit=50&sort_by=free_currency"),
      api.get("/settings"),
      api.get("/admin/files"),
    ]);

    const nextCurrencies = c.status === "fulfilled"
      ? normalizeApiList(c.value.data, "currencies")
        .filter((item) => item && typeof item === "object")
        .map(normalizeCurrency)
      : [];
    const nextViewers = v.status === "fulfilled"
      ? normalizeApiList(v.value.data, "viewers")
        .filter((item) => item && typeof item === "object")
        .map(normalizeViewer)
      : [];
    const nextSettings = s.status === "fulfilled" && s.value.data && typeof s.value.data === "object"
      ? s.value.data
      : {};
    const nextRuntimeFiles = f.status === "fulfilled"
      ? normalizeApiList(f.value.data?.runtime_media, "runtime_media")
      : [];

    setList(nextCurrencies);
    setViewers(nextViewers);
    setSettings(nextSettings);
    setRuntimeFiles(nextRuntimeFiles);

    const failed = [
      c.status === "rejected" ? "currencies" : "",
      v.status === "rejected" ? "viewers" : "",
      s.status === "rejected" ? "settings" : "",
      f.status === "rejected" ? "files" : "",
    ].filter(Boolean);
    if (failed.length) {
      setLoadError(`Could not load ${failed.join(", ")}. Some currency tools may be limited until refresh.`);
    }

    if (!nextCurrencies.find((item) => item.id === grant.currency_id) && nextCurrencies[0]) {
      setGrant((current) => ({ ...current, currency_id: nextCurrencies[0].id }));
    }
  }, [grant.currency_id]);
  useEffect(() => { load(); }, [load]);

  const safeCurrencyList = useMemo(() => list.filter((currency) => currency && typeof currency === "object" && currency.id), [list]);
  const safeViewerList = useMemo(() => viewers.filter((viewer) => viewer && typeof viewer === "object"), [viewers]);
  const visibleCurrencies = useMemo(() => safeCurrencyList.filter((c) => c.visible !== false || c.id === "giveaway_currency"), [safeCurrencyList]);
  const earningEstimates = useMemo(() => {
    const free = safeCurrencyList.find((currency) => currency.id === "free_currency") || {};
    const giveaway = settings?.giveaway_token_rewards || {};
    const event = settings?.event_token_rewards || {};
    const streak = settings?.streak_community_rewards || {};
    const watchPerHour = Math.max(0, toNumber(free.earn_per_minute_watchtime, 2) * 60);
    const chatCooldown = Math.max(1, toNumber(free.message_cooldown_seconds, 30));
    const chatPerHour = Math.floor(3600 / chatCooldown) * Math.max(0, toNumber(free.earn_per_message, 1));
    const giveawayMessageAmount = toNumber(giveaway.message_amount, 1);
    const giveawayPerHour = giveawayMessageAmount > 0 ? Math.floor(3600 / Math.max(1, toNumber(giveaway.message_cooldown_seconds, 3600))) * giveawayMessageAmount : 0;
    const eventMessageAmount = toNumber(event.message_amount, 1);
    const eventPerHour = eventMessageAmount > 0 ? Math.floor(3600 / Math.max(1, toNumber(event.message_cooldown_seconds, 900))) * eventMessageAmount : 0;
    return [
      {
        title: currencyLabel(free || { id: "free_currency" }),
        text: `${watchPerHour}-${watchPerHour + chatPerHour} per active hour`,
        detail: `${watchPerHour} from active watchtime, up to ${chatPerHour} from chat cooldowns.`,
      },
      {
        title: "Stream streaks",
        text: `Up to ${streak.max_streak ?? 100} per active chatter`,
        detail: `Stream streaks can add up to ${streak.max_streak ?? 100} Bunny Tokens to each active chatter.`,
      },
      {
        title: "Giveaway Tokens",
        text: `${giveaway.first_message_amount ?? 1} first message, then about ${giveawayPerHour}/hour`,
        detail: `${giveaway.sub_amount ?? 1} per sub and ${giveaway.gift_sub_amount ?? 1} per gifted sub when enabled.`,
      },
      {
        title: "Event Tokens",
        text: `${event.first_message_amount ?? 5} first message, then about ${eventPerHour}/hour`,
        detail: `${event.sub_amount ?? 5} per sub and ${event.gift_sub_amount ?? 5} per gifted sub when enabled.`,
      },
    ];
  }, [safeCurrencyList, settings]);

  const updateRewardSettings = (key, patch) => {
    setSettings((current) => ({
      ...(current || {}),
      [key]: {
        ...((current || {})[key] || {}),
        ...patch,
      },
    }));
  };

  const saveRewardSettings = async (key) => {
    const { data } = await api.patch("/settings", { [key]: settings?.[key] || {} });
    setSettings(data);
    toast.success("Saved reward settings");
  };

  const save = async (c) => {
    const payload = {
      name: c.name,
      abbreviation: c.abbreviation,
      icon: c.icon,
      color: c.color,
      description: c.description,
      enabled: c.enabled !== false,
      visible: c.id === "giveaway_currency" ? true : c.visible !== false,
      earn_per_message: c.earn_per_message,
      message_cooldown_seconds: c.message_cooldown_seconds,
      earn_per_minute_watchtime: c.earn_per_minute_watchtime,
      earn_per_bit: c.earn_per_bit,
      earn_per_sub: c.earn_per_sub,
      earn_per_gift_sub: c.earn_per_gift_sub,
    };
    await api.patch(`/currencies/${c.id}`, payload);
    toast.success(`Saved ${c.name}`);
    load();
  };

  const doGrant = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/currency/grant", {
        twitch_user_id: grant.viewer_id,
        currency_id: grant.currency_id,
        amount: grant.amount,
        reason: grant.reason,
      });
      toast.success(`Granted ${grant.amount}`);
      setLastGrant(data);
      setGrant({ ...grant, viewer_id: "", amount: 0, reason: "" });
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Grant failed");
    }
  };

  const doReset = async () => {
    try {
      const payload = {
        confirmation: reset.confirmation,
        currency_id: reset.currency_id || null,
        login: reset.scope === "user" ? reset.login : null,
      };
      const path = reset.scope === "user" ? "/admin/currencies/reset-user" : "/admin/currencies/reset-all";
      await api.post(path, payload);
      toast.success("Currency reset complete");
      setReset({ login: "", currency_id: "", scope: "user", confirmation: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset blocked");
    }
  };

  return (
    <div className="space-y-6" data-testid="currencies-page">
      <div>
        <div className="label-eyebrow">Channel points</div>
        <h1 className="text-2xl font-semibold tracking-tight mt-1">Currencies</h1>
        <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">Display fix build: currencies-safe-render-v2</div>
      </div>

      {loadError && (
        <div className="surface p-4 text-sm text-amber-200 border border-amber-500/30 bg-amber-500/10">
          {loadError}
        </div>
      )}

      <SectionErrorBoundary name="Currency cards">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {safeCurrencyList.map((c) => (
            <SectionErrorBoundary key={c.id} name={`${currencyLabel(c)} card`}>
              <Card c={c} setC={(updated) => setList(list.map((x) => x?.id === c.id ? normalizeCurrency(updated) : x))} onSave={save} />
            </SectionErrorBoundary>
          ))}
        </div>
      </SectionErrorBoundary>

      {settings && (
        <SectionErrorBoundary name="Reward balancing">
          <div className="surface p-5">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <div className="label-eyebrow">Twitch earning rules</div>
              <h2 className="text-lg font-semibold mt-1">Reward balancing</h2>
            </div>
            <Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" />
          </div>
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {earningEstimates.map((estimate) => (
              <div key={estimate.title} className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-3">
                <div className="label-eyebrow">{estimate.title}</div>
                <div className="mt-1 text-sm font-semibold">{estimate.text}</div>
                <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{estimate.detail}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <TokenRewardPanel
              icon={Ticket}
              title="Giveaway Tokens"
              config={settings.giveaway_token_rewards || {}}
              onChange={(patch) => updateRewardSettings("giveaway_token_rewards", patch)}
              onSave={() => saveRewardSettings("giveaway_token_rewards")}
            />
            <TokenRewardPanel
              icon={Sparkles}
              title="Event Tokens"
              config={settings.event_token_rewards || {}}
              onChange={(patch) => updateRewardSettings("event_token_rewards", patch)}
              onSave={() => saveRewardSettings("event_token_rewards")}
            />
            <StreakRewardPanel
              config={settings.streak_community_rewards || {}}
              currencies={safeCurrencyList}
              runtimeFiles={runtimeFiles}
              onChange={(patch) => updateRewardSettings("streak_community_rewards", patch)}
              onSave={() => saveRewardSettings("streak_community_rewards")}
              onFilesChanged={load}
            />
          </div>
          </div>
        </SectionErrorBoundary>
      )}

      <SectionErrorBoundary name="Manual grant">
        <div className="surface p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
          <div>
            <div className="label-eyebrow">Manual grant</div>
            <h2 className="text-lg font-semibold mt-1">Give currency to a viewer</h2>
            <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
              Use this for corrections, prizes, make-goods, or one-off streamer rewards. Grants are recorded in the ledger with a reason.
            </p>
          </div>
          <Gift className="w-5 h-5 text-[hsl(var(--primary))]" />
        </div>
        <form onSubmit={doGrant} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end" data-testid="admin-currency-grant-form">
          <Select
            label="Viewer"
            value={grant.viewer_id}
            onChange={(v) => setGrant({ ...grant, viewer_id: v })}
            options={[["", "Choose viewer"], ...safeViewerList.map((viewer) => [viewer.twitch_user_id, viewer.display_name || viewer.login || viewer.twitch_user_id])]}
            test="grant-viewer-id"
          />
          <Input label="Amount" type="number" value={grant.amount} onChange={(v) => setGrant({ ...grant, amount: parseInt(v || 0, 10) })} dataTestId="grant-amount" />
          <Select label="Currency" value={grant.currency_id} onChange={(v) => setGrant({ ...grant, currency_id: v })} options={safeCurrencyList.map((c) => [c.id, currencyLabel(c)])} test="grant-currency" />
          <div className="md:col-span-2">
            <Input label="Reason" value={grant.reason} onChange={(v) => setGrant({ ...grant, reason: v })} required dataTestId="grant-reason" />
          </div>
          <button
            type="submit"
            disabled={!grant.viewer_id || !grant.reason || Number(grant.amount || 0) <= 0}
            data-testid="grant-submit"
            className="bg-[hsl(var(--primary))] text-white text-sm px-4 py-2 rounded flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Gift className="w-4 h-4" /> Grant
          </button>
        </form>
        {lastGrant?.grant && (
          <div className="mt-4 rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] px-4 py-3 text-sm">
            <div className="label-eyebrow mb-1">Last grant</div>
            <div className="text-[hsl(var(--foreground))]">
              {currencyAmountLabel(lastGrant.grant.amount, safeCurrencyList.find((currency) => currency.id === lastGrant.grant.currency_id) || { id: lastGrant.grant.currency_id })} granted to {lastGrant.display_name || lastGrant.login || lastGrant.viewer_id}.
            </div>
            <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
              Source: {lastGrant.grant.source || "admin_grant"} - Reason: {lastGrant.grant.reason || "No reason"}
            </div>
          </div>
        )}
        </div>
      </SectionErrorBoundary>

      <SectionErrorBoundary name="Currency reset tools">
        <details className="surface p-5 group">
        <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
          <span>
            <span className="label-eyebrow">Danger zone</span>
            <span className="text-lg font-semibold mt-1 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[hsl(var(--destructive))]" /> Currency reset tools</span>
          </span>
          <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] group-open:hidden">Open</span>
        </summary>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <Select label="Scope" value={reset.scope} onChange={(v) => setReset({ ...reset, scope: v })} options={[["user", "One user"], ["all", "All users"]]} />
          {reset.scope === "user" && <Input label="Viewer login" value={reset.login} onChange={(v) => setReset({ ...reset, login: v })} dataTestId="reset-login" />}
          <Select label="Currency" value={reset.currency_id} onChange={(v) => setReset({ ...reset, currency_id: v })} options={[["", "All currency"], ...safeCurrencyList.map((c) => [c.id, currencyLabel(c)])]} />
          <Input label="Confirmation" value={reset.confirmation} onChange={(v) => setReset({ ...reset, confirmation: v })} dataTestId="reset-currency-confirmation" />
          <button
            onClick={doReset}
            disabled={reset.confirmation !== RESET_TEXT || (reset.scope === "user" && !reset.login)}
            className="bg-[hsl(var(--destructive))] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm px-4 py-2 rounded flex items-center justify-center gap-2"
            data-testid="reset-currency-submit"
          >
            <Trash2 className="w-4 h-4" /> Reset
          </button>
        </div>
        </details>
      </SectionErrorBoundary>

      <SectionErrorBoundary name="Leaderboard">
        <div className="surface p-5 overflow-x-auto">
        <div className="label-eyebrow mb-3">Full leaderboard</div>
        <table className="w-full text-sm font-mono">
          <thead>
            <tr className="text-left text-[hsl(var(--muted-foreground))] text-xs uppercase tracking-wider">
              <th className="py-2">#</th>
              <th>Viewer</th>
              {visibleCurrencies.map((c) => <th key={c.id} className="text-right min-w-28" style={{ color: safeColor(c.color, c.id) }}>{currencyLabel(c)}</th>)}
              <th className="text-right">Active Watchtime</th>
            </tr>
          </thead>
          <tbody>
            {safeViewerList.map((v, i) => (
              <tr key={v.twitch_user_id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-2))]">
                <td className="py-2 text-[hsl(var(--muted-foreground))]">{i + 1}</td>
                <td>{v.display_name}</td>
                {visibleCurrencies.map((c) => <td key={c.id} className="text-right" style={{ color: safeColor(c.color, c.id) }}>{v.balances?.[c.id] || 0}</td>)}
                <td className="text-right text-[hsl(var(--muted-foreground))]">{v.watchtime_minutes}m</td>
              </tr>
            ))}
            {safeViewerList.length === 0 && <tr><td colSpan={visibleCurrencies.length + 3} className="py-6 text-center text-[hsl(var(--muted-foreground))]">No viewers yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </SectionErrorBoundary>
    </div>
  );
}

class SectionErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error(`Currencies section failed: ${this.props.name}`, error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="surface p-4 border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-sm">
          <div className="label-eyebrow text-[hsl(var(--destructive))]">Currencies section error</div>
          <div className="mt-2 font-semibold">{this.props.name || "Section"} could not load.</div>
          <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
            {this.state.error?.message || "Unknown display error"}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function Card({ c, setC, onSave }) {
  const color = safeColor(c.color, c.id);
  return (
    <div className={`surface p-5 transition-opacity ${c.enabled === false ? "opacity-60" : ""}`} data-testid={`currency-edit-${c.id}`}>
      <div className="flex items-center justify-between">
        <span className="label-eyebrow" style={{ color }}>{TYPE_LABELS[c.kind] || c.kind}</span>
        <CurrencyIcon currency={{ ...c, color }} />
      </div>
      <input value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} className="mt-2 w-full bg-transparent text-xl font-semibold border-b border-[hsl(var(--border))] focus:outline-none focus:border-[hsl(var(--primary))] py-1" data-testid={`currency-${c.id}-name`} />
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <Text label="Abbreviation" value={c.abbreviation || ""} onChange={(v) => setC({ ...c, abbreviation: v })} />
        <ColorPicker label="Color" value={color} onChange={(v) => setC({ ...c, color: v })} fallback={FALLBACK_COLORS[c.id] || "#0A84FF"} test={`currency-color-${c.id}`} />
        <Toggle label="Enabled" checked={c.enabled !== false} onChange={(v) => setC({ ...c, enabled: v })} />
        <Toggle label="Visible" checked={c.id === "giveaway_currency" || c.visible !== false} disabled={c.id === "giveaway_currency"} onChange={(v) => setC({ ...c, visible: v })} />
      </div>
      <div className="mt-4">
        <IconPicker label="Icon" value={c.icon} color={color} onChange={(icon) => setC({ ...c, icon })} test={`currency-icon-${c.id}`} />
      </div>
      {c.id === "free_currency" && (
        <div className="mt-4 space-y-3 text-sm">
          <NumberField label="Per message" value={c.earn_per_message} onChange={(v) => setC({ ...c, earn_per_message: v })} />
          <NumberField label="Cooldown (seconds)" value={c.message_cooldown_seconds} onChange={(v) => setC({ ...c, message_cooldown_seconds: v })} />
          <NumberField label="Per minute active watchtime" value={c.earn_per_minute_watchtime} onChange={(v) => setC({ ...c, earn_per_minute_watchtime: v })} />
        </div>
      )}
      {c.id === "paid_currency" && (
        <div className="mt-4 space-y-3 text-sm">
          <NumberField label="Per bit" value={c.earn_per_bit} onChange={(v) => setC({ ...c, earn_per_bit: v })} />
          <NumberField label="Per sub" value={c.earn_per_sub} onChange={(v) => setC({ ...c, earn_per_sub: v })} />
          <NumberField label="Per gift sub" value={c.earn_per_gift_sub} onChange={(v) => setC({ ...c, earn_per_gift_sub: v })} />
        </div>
      )}
      <div className="mt-4">
        <div className="label-eyebrow mb-1">Description</div>
        <textarea value={c.description || ""} onChange={(e) => setC({ ...c, description: e.target.value })} rows={2} className="w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm" />
      </div>
      <button onClick={() => onSave(c)} className="mt-4 w-full flex items-center justify-center gap-2 text-xs uppercase tracking-widest border border-[hsl(var(--border))] py-2 rounded hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]" data-testid={`save-currency-${c.id}`}>
        <Save className="w-3 h-3" /> Save
      </button>
    </div>
  );
}

function TokenRewardPanel({ icon: Icon, title, config, onChange, onSave }) {
  const PanelIcon = typeof Icon === "function" ? Icon : Sparkles;
  return (
    <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <PanelIcon className="w-4 h-4 text-[hsl(var(--primary))]" />
          <div className="font-semibold">{title}</div>
        </div>
        <Toggle label="Enabled" checked={config.enabled === true} onChange={(v) => onChange({ enabled: v })} />
      </div>
      <div className="mt-4 space-y-3 text-sm">
        <NumberField label="First message" value={config.first_message_amount} onChange={(v) => onChange({ first_message_amount: v })} />
        <NumberField label="Per message" value={config.message_amount} onChange={(v) => onChange({ message_amount: v })} />
        <NumberField label="Cooldown seconds" value={config.message_cooldown_seconds} onChange={(v) => onChange({ message_cooldown_seconds: v })} />
        <NumberField label="Per sub" value={config.sub_amount} onChange={(v) => onChange({ sub_amount: v })} />
        <NumberField label="Per gifted sub" value={config.gift_sub_amount} onChange={(v) => onChange({ gift_sub_amount: v })} />
      </div>
      <button onClick={onSave} className="mt-4 w-full flex items-center justify-center gap-2 text-xs uppercase tracking-widest border border-[hsl(var(--border))] py-2 rounded hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]">
        <Save className="w-3 h-3" /> Save
      </button>
    </div>
  );
}

function StreakRewardPanel({ config, currencies, runtimeFiles, onChange, onSave, onFilesChanged }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const audioFiles = (runtimeFiles || []).filter((file) => file.media_type === "audio");
  const currentUrl = config.sound_url || "";
  const currentInList = !currentUrl || audioFiles.some((file) => file.url === currentUrl);

  const uploadAudio = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/admin/runtime-media/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.media_type !== "audio") throw new Error("Choose an audio file for streak rewards.");
      onChange({ sound_id: "", sound_url: data.media_url });
      await onFilesChanged?.();
      toast.success("Streak reward sound uploaded.");
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || "Could not upload streak sound.");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setUploading(false);
    }
  };

  return (
    <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-[hsl(var(--primary))]" />
          <div className="font-semibold">Streak Rewards</div>
        </div>
        <Toggle label="Enabled" checked={config.enabled === true} onChange={(v) => onChange({ enabled: v })} />
      </div>
      <div className="mt-4 space-y-3 text-sm">
        <Select label="Reward currency" value={config.currency_id || "free_currency"} onChange={(v) => onChange({ currency_id: v })} options={currencies.map((c) => [c.id, currencyLabel(c)])} />
        <NumberField label="Active window seconds" value={config.active_window_seconds} onChange={(v) => onChange({ active_window_seconds: v })} />
        <NumberField label="Reward cap" value={config.max_streak} onChange={(v) => onChange({ max_streak: v })} />
        <div className="rounded border border-[hsl(var(--border))] bg-black/20 p-3 text-xs text-[hsl(var(--muted-foreground))]">
          Each active chatter gets Bunny Tokens equal to the stream streak number, capped at the reward cap.
        </div>
        <div className="rounded border border-[hsl(var(--border))] bg-black/20 p-3 space-y-3">
          <div>
            <div className="label-eyebrow mb-1.5">Reward sound</div>
            <select
              value={currentUrl}
              onChange={(event) => onChange({ sound_id: "", sound_url: event.target.value })}
              className="w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm"
            >
              <option value="">No sound selected</option>
              {!currentInList && <option value={currentUrl}>{currentUrl}</option>}
              {audioFiles.map((file) => (
                <option key={file.reference} value={file.url}>{file.name}</option>
              ))}
            </select>
            <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))] break-all">
              {currentUrl || "Upload or choose an audio file from /app/files."}
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg"
            className="hidden"
            onChange={(event) => uploadAudio(event.target.files?.[0])}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 rounded bg-[hsl(var(--primary))] px-3 py-2 text-xs uppercase tracking-widest text-white disabled:opacity-50"
          >
            <Edit3 className="w-3 h-3" /> {uploading ? "Uploading..." : "Upload audio"}
          </button>
        </div>
        <Toggle label="Chat message" checked={config.chat_message_enabled !== false} onChange={(v) => onChange({ chat_message_enabled: v })} />
      </div>
      <div className="mt-3 flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))]">
        <Volume2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>A 7 stream streak gives 7 tokens; a 100+ stream streak gives 100 tokens to each active chatter.</span>
      </div>
      <button onClick={onSave} className="mt-4 w-full flex items-center justify-center gap-2 text-xs uppercase tracking-widest border border-[hsl(var(--border))] py-2 rounded hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]">
        <Save className="w-3 h-3" /> Save
      </button>
    </div>
  );
}

function Toggle({ label = "", checked, onChange, disabled }) {
  return (
    <label className={`flex items-center justify-between gap-3 ${disabled ? "opacity-50" : "cursor-pointer"}`}>
      <span className="text-[hsl(var(--muted-foreground))]">{label}</span>
      <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} />
    </label>
  );
}

function NumberField({ label = "", value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[hsl(var(--muted-foreground))]">{label}</span>
      <input type="number" min={0} value={value ?? 0} onChange={(e) => onChange?.(parseInt(e.target.value || 0, 10))} className="w-24 bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-2 py-1 text-right font-mono text-sm" />
    </div>
  );
}

function Text({ label = "", value, onChange }) {
  return <Input label={label} value={value} onChange={onChange} />;
}

function Select({ label = "", value = "", onChange, options, test }) {
  const safeOptions = Array.isArray(options)
    ? options
      .filter((option) => Array.isArray(option))
      .map(([id, labelText]) => [String(id ?? ""), String(labelText ?? id ?? "Option")])
    : [];
  return (
    <div>
      <div className="label-eyebrow mb-1.5">{label}</div>
      <select value={value ?? ""} onChange={(e) => onChange?.(e.target.value)} className="w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm" data-testid={test}>
        {safeOptions.map(([id, labelText], index) => <option key={id || `option-${index}`} value={id}>{labelText}</option>)}
      </select>
    </div>
  );
}

function Input({ label = "", value = "", onChange, type = "text", required, dataTestId }) {
  return (
    <div>
      <div className="label-eyebrow mb-1.5">{label}</div>
      <input type={type} value={value ?? ""} onChange={(e) => onChange?.(e.target.value)} required={required} data-testid={dataTestId} className="w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm" />
    </div>
  );
}

function normalizeApiList(data, key) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  return [];
}

function normalizeCurrency(currency) {
  const id = String(currency?.id || currency?.kind || "currency").trim() || "currency";
  return {
    ...currency,
    id,
    kind: String(currency?.kind || id),
    name: String(currency?.name || currencyLabel({ id })),
    abbreviation: String(currency?.abbreviation || ""),
    icon: String(currency?.icon || ""),
    color: safeColor(currency?.color, id),
    description: String(currency?.description || ""),
    balances: currency?.balances && typeof currency.balances === "object" ? currency.balances : {},
  };
}

function normalizeViewer(viewer) {
  const twitchUserId = String(viewer?.twitch_user_id || viewer?.id || viewer?.login || "");
  return {
    ...viewer,
    twitch_user_id: twitchUserId,
    login: String(viewer?.login || twitchUserId),
    display_name: String(viewer?.display_name || viewer?.login || twitchUserId || "Viewer"),
    balances: viewer?.balances && typeof viewer.balances === "object" ? viewer.balances : {},
    watchtime_minutes: toNumber(viewer?.watchtime_minutes, 0),
  };
}

function safeColor(value, currencyId = "") {
  const raw = String(value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw) || raw.startsWith("hsl(") || raw.startsWith("rgb(")) return raw;
  return FALLBACK_COLORS[currencyId] || "#0A84FF";
}
